import type { VerticalType } from '../../types/cmsTypes';
import { normalizeCompareIds } from './compareUrl';

/**
 * What the results page leaves behind for the comparison page, and the comparison page for the
 * way back: the selection, by product id, filed under the search it was made for.
 */
export type CompareStored = {
  /** Identity of the search these plans were priced for; a different one drops the record. */
  scope: string;
  selected: string[];
};

export const compareStorageKey = (vertical: VerticalType) =>
  `compare:${vertical}`;

export function readCompareStorage(
  vertical: VerticalType,
): CompareStored | null {
  try {
    const raw = window.sessionStorage.getItem(compareStorageKey(vertical));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CompareStored>;
    if (!Array.isArray(parsed.selected)) return null;
    return {
      scope: parsed.scope ?? '',
      selected: normalizeCompareIds(parsed.selected),
    };
  } catch {
    return null;
  }
}

/**
 * `null` forgets the record (it was made for another search). An empty selection is kept as a
 * record of its own: "every plan was removed for this search" is news the results page must hear
 * on the way back, where "no record" would let its stale URL re-select them.
 */
export function writeCompareStorage(
  vertical: VerticalType,
  value: CompareStored | null,
): void {
  try {
    const key = compareStorageKey(vertical);
    if (!value) {
      window.sessionStorage.removeItem(key);
      return;
    }
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or blocked storage: the URL still carries the selection.
  }
}
